import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractFormControl } from '../abstract-form-control';
import { CustomFieldCompositionDto } from '@artemis-software/wr-api';
import { TableColumnConfig, TableColumnType, TableConfig, TableRowConfig } from '@shared/custom-form/table-config';

@Component({
  standalone: false,
  selector: 'wr-custom-field-composition-control',
  templateUrl: './custom-field-composition-control.component.html',
  styleUrls: ['./custom-field-composition-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CustomFieldCompositionControlComponent,
      multi: true,
    },
  ],
})
export class CustomFieldCompositionControlComponent extends AbstractFormControl<CustomFieldCompositionDto> implements OnInit {

  readonly cardinalityTypes = Object.values(CustomFieldCompositionDto.CardinalityTypeEnum);
  readonly valueTypes = Object.values(CustomFieldCompositionDto.ValueTypeEnum);
  readonly units = Object.values(CustomFieldCompositionDto.UnitEnum);

  readonly ValueTypeEnum = CustomFieldCompositionDto.ValueTypeEnum;

  form = new FormGroup({
    valueType: new FormControl<CustomFieldCompositionDto.ValueTypeEnum | null>(null),
    cardinalityType: new FormControl<CustomFieldCompositionDto.CardinalityTypeEnum | null>(null),
    unit: new FormControl<CustomFieldCompositionDto.UnitEnum | null>(null),
    multipleChoiceOptions: new FormControl<string[]>([]),
  });

  newOption = '';

  readonly tableColumnTypes: TableColumnType[] = ['String', 'Integer', 'Double', 'Boolean', 'Dropdown'];
  tableColumns: (TableColumnConfig & { _newDropdownOption: string })[] = [];
  tableRows: TableRowConfig[] = [];
  allowAddRows = false;

  get isTable(): boolean {
    return this.form.value.valueType === CustomFieldCompositionDto.ValueTypeEnum.Table;
  }

  ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      this.emitValue();
    });
  }

  emitValue(): void {
    const raw = this.form.getRawValue();
    if (raw.valueType && raw.cardinalityType) {
      this.value = {
        valueType: raw.valueType,
        cardinalityType: raw.cardinalityType,
        unit: raw.unit ?? undefined,
        multipleChoiceOptions: raw.multipleChoiceOptions ?? [],
        tableConfig: this.isTable ? this.buildTableConfig() : undefined,
      };
    }
  }

  private buildTableConfig() {
    return {
      columns: this.tableColumns.map(c => ({ name: c.name, type: c.type, options: c.options ?? [] })),
      rows: this.tableRows.map(r => ({ name: r.name })),
      allowAddRows: this.allowAddRows,
    };
  }

  addTableColumn(): void {
    this.tableColumns = [...this.tableColumns, { name: '', type: 'String', options: [], _newDropdownOption: '' }];
    this.emitValue();
  }

  removeTableColumn(index: number): void {
    this.tableColumns = this.tableColumns.filter((_, i) => i !== index);
    this.emitValue();
  }

  updateTableColumn(index: number, field: 'name' | 'type', value: string): void {
    const col = { ...this.tableColumns[index], [field]: value };
    this.tableColumns = this.tableColumns.map((c, i) => i === index ? col : c);
    this.emitValue();
  }

  addDropdownOption(colIndex: number): void {
    const col = this.tableColumns[colIndex];
    const trimmed = col._newDropdownOption.trim();
    if (!trimmed) return;
    const updated = { ...col, options: [...(col.options ?? []), trimmed], _newDropdownOption: '' };
    this.tableColumns = this.tableColumns.map((c, i) => i === colIndex ? updated : c);
    this.emitValue();
  }

  removeDropdownOption(colIndex: number, optIndex: number): void {
    const col = this.tableColumns[colIndex];
    const updated = { ...col, options: col.options?.filter((_, i) => i !== optIndex) ?? [] };
    this.tableColumns = this.tableColumns.map((c, i) => i === colIndex ? updated : c);
    this.emitValue();
  }

  addTableRow(): void {
    this.tableRows = [...this.tableRows, { name: '' }];
    this.emitValue();
  }

  removeTableRow(index: number): void {
    this.tableRows = this.tableRows.filter((_, i) => i !== index);
    this.emitValue();
  }

  updateTableRow(index: number, name: string): void {
    this.tableRows = this.tableRows.map((r, i) => i === index ? { name } : r);
    this.emitValue();
  }

  toggleAllowAddRows(): void {
    this.allowAddRows = !this.allowAddRows;
    this.emitValue();
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  override writeValue(value: CustomFieldCompositionDto | undefined): void {
    super.writeValue(value);
    if (value) {
      this.form.setValue({
        valueType: value.valueType ?? null,
        cardinalityType: value.cardinalityType ?? null,
        unit: value.unit ?? null,
        multipleChoiceOptions: value.multipleChoiceOptions ?? [],
      }, { emitEvent: false });
      if (value.tableConfig) {
        const tc = value.tableConfig as TableConfig;
        this.tableColumns = (tc.columns ?? []).map(c => ({ ...c, _newDropdownOption: '' }));
        this.tableRows = tc.rows ?? [];
        this.allowAddRows = tc.allowAddRows ?? false;
      } else {
        this.tableColumns = [];
        this.tableRows = [];
        this.allowAddRows = false;
      }
    }
  }

  get isMultipleChoice(): boolean {
    return this.form.value.valueType === CustomFieldCompositionDto.ValueTypeEnum.MultipleChoice;
  }

  get hasUnit(): boolean {
    const type = this.form.value.valueType;
    return type === CustomFieldCompositionDto.ValueTypeEnum.Integer ||
      type === CustomFieldCompositionDto.ValueTypeEnum.Double;
  }

  get multipleChoiceOptions(): string[] {
    return this.form.value.multipleChoiceOptions ?? [];
  }

  addOption(): void {
    const trimmed = this.newOption.trim();
    if (trimmed) {
      const current = this.form.value.multipleChoiceOptions ?? [];
      this.form.patchValue({ multipleChoiceOptions: [...current, trimmed] });
      this.newOption = '';
    }
  }

  removeOption(index: number): void {
    const current = [...(this.form.value.multipleChoiceOptions ?? [])];
    current.splice(index, 1);
    this.form.patchValue({ multipleChoiceOptions: current });
  }
}
